import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiError, ApiService, LoggerService } from '@phx/core';
import { Observable, catchError, of, tap, throwError } from 'rxjs';
import { DhlAuthInfo, DhlAuthTokenRequest, DhlLoginUrl } from './dhl-onboarding-data';

@Injectable({
    providedIn: 'root'
})
export class DhlOnboardingService {
    constructor(
        private api: ApiService,
        private logger: LoggerService
    ) {}

    getDhlAuthInfo(): Observable<DhlAuthInfo> {
        return this.api.get<DhlAuthInfo>('logistics/dhl-germany/auth/info').pipe(
            tap({
                next: response =>
                    this.logger.debug(`[LOGISTICS-SERVICE] get DhlAuthInfo successful`, ['response', response]),
                error: error => this.logger.debug(`[LOGISTICS-SERVICE] get DhlAuthInfo failed`, ['error', error])
            }),
            catchError(error => {
                // the backend answers with 404 (without a response body) as long as the merchant has not
                // completed the DHL login, which is a regular state for us and not an error
                if (getErrorStatus(error) === 404) {
                    return of<DhlAuthInfo>({ status: 'NOT_CONNECTED', expiresAt: null });
                }

                return throwError(() => error);
            })
        );
    }

    getDhlLoginUrl(): Observable<DhlLoginUrl> {
        return this.api.get<DhlLoginUrl>('logistics/dhl-germany/auth/login-url').pipe(
            tap({
                next: response =>
                    this.logger.debug(`[MERCHANT-SERVICE] get DhlLoginUrl successful`, ['response', response]),
                error: error => this.logger.debug(`[MERCHANT-SERVICE] get DhlLoginUrl failed`, ['error', error])
            })
        );
    }

    completeDhlLogin(request: DhlAuthTokenRequest): Observable<DhlAuthInfo> {
        return this.api.post<DhlAuthInfo>('logistics/dhl-germany/auth/token', undefined, request).pipe(
            tap({
                next: response =>
                    this.logger.debug(`[MERCHANT-SERVICE] post completeDhlLogin successful`, ['response', response]),
                error: error => this.logger.debug(`[MERCHANT-SERVICE] post completeDhlLogin failed`, ['error', error])
            })
        );
    }
}

/**
 * The HTTP status is read from an `ApiError` when the error interceptor converted it, and from the raw
 * `HttpErrorResponse` otherwise (the interceptor is not registered in this app).
 */
function getErrorStatus(error: unknown): number | undefined {
    if (error instanceof ApiError) {
        return error.code;
    }

    if (error instanceof HttpErrorResponse) {
        return error.status;
    }

    return undefined;
}
